const PLACEHOLDER_PATTERN = /\{(@?[A-Z0-9_#]+)\}/gi;
const BRACKET_REFERENCE_PATTERN = /\[(@?[A-Z0-9_#]+)\]/gi;
const DEFAULT_COINCIDENCE = '[\\w\\s\\d¤*/@¥,.-]+';

const DATATYPE_MAP = {
  ANY: 'text',
  ALPHA: 'text',
  CHAR: 'text',
  DECIMAL: 'decimal',
  NUM: 'numeric',
  NUMBER: 'numeric',
  NUMERIC: 'numeric',
};

function compactObject(value) {
  return Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => {
      if (entryValue === undefined || entryValue === null) {
        return false;
      }

      if (Array.isArray(entryValue) && entryValue.length === 0) {
        return false;
      }

      return true;
    })
  );
}

function normalizeVariableName(variableName = '') {
  return variableName.replace(/#[A-Z]$/i, '');
}

function normalizeDomName(name = '') {
  return normalizeVariableName(name).replace(/^@/, '');
}

function normalizeLookupName(name = '') {
  return normalizeVariableName(String(name || '')).replace(/^@/, '').toUpperCase();
}

function normalizeTypedata(field = {}, variable = null) {
  if (field.type === 'display' || field.controlType === 'label' || field.control === 'display') {
    return 'label';
  }

  if (field.type === 'button') {
    return 'button';
  }

  if (field.type === 'select' || field.controlType === 'select' || field.control === 'select' || field.suffix === '#D') {
    return 'select';
  }

  if (field.type === 'checkbox-group') {
    return 'check_group';
  }

  if (field.controlType === 'checkbox' || field.control === 'checkbox' || field.suffix === '#C') {
    return 'check';
  }

  const datatype = (field.datatype || variable?.datatype || '').toUpperCase();

  return DATATYPE_MAP[datatype] || 'text';
}

function findVariable(ir, variableName) {
  if (!variableName) {
    return null;
  }

  const normalizedName = normalizeVariableName(variableName).toUpperCase();

  return (
    Object.values(ir.variables || {}).find((variable) => {
      return normalizeVariableName(variable.name).toUpperCase() === normalizedName;
    }) || null
  );
}

function findDefault(ir, variableName) {
  const normalizedName = normalizeVariableName(variableName).toUpperCase();

  return (ir.defaults || []).find((defaultNode) => {
    return normalizeVariableName(defaultNode.target || '').toUpperCase() === normalizedName;
  });
}

function getInitialValue(ir, variableName) {
  const defaultNode = findDefault(ir, variableName);

  if (!defaultNode) {
    return '';
  }

  if (defaultNode.defaultType === 'default_value' || defaultNode.defaultType === 'default_checkbox') {
    return defaultNode.value ?? '';
  }

  return '';
}

function getFieldOptions(field = {}) {
  return field.options || field.datasource || [];
}

function normalizeDatasource(options = [], context = null, scopeId = '') {
  const normalizedOptions = options.map((option) => ({
    text: option.text || option.label || option.value,
    value: option.value,
  }));

  return dedupeDatasourceOptions(normalizedOptions, context, scopeId);
}

function createDuplicateReport() {
  return {
    formsSkipped: 0,
    fieldsSkipped: 0,
    optionsSkipped: 0,
    actionsSkipped: 0,
    commandsSkipped: 0,
  };
}

function addDiagnostic(diagnostics, diagnostic) {
  diagnostics.push(diagnostic);
}

function normalizeDatasourceOption(option = {}) {
  return {
    text: option.text || option.label || option.value,
    value: option.value ?? '',
    actionType: option.actionType,
    target: option.target,
    actionRef: option.actionRef,
  };
}

function datasourceOptionSignature(option = {}) {
  return JSON.stringify({
    text: option.text || '',
    value: option.value ?? '',
    actionType: option.actionType || '',
    target: option.target || '',
    actionRef: option.actionRef || '',
  });
}

function uniqueDatasourceValue(option, usedValues) {
  const base = String(option.value || safeMenuValue(option.text) || 'OPTION');
  let candidate = base;
  let index = 2;

  while (usedValues.has(candidate)) {
    candidate = `${base}_${index}`;
    index += 1;
  }

  return candidate;
}

function dedupeDatasourceOptions(options = [], context = null, scopeId = '') {
  const usedValues = new Set();
  const valueSignatures = new Map();
  const normalizedOptions = [];

  options.forEach((rawOption) => {
    const option = normalizeDatasourceOption(rawOption);
    let value = String(option.value ?? '');
    const signature = datasourceOptionSignature(option);

    if (usedValues.has(value)) {
      const previousSignature = valueSignatures.get(value);

      if (previousSignature === signature) {
        context?.duplicates && (context.duplicates.optionsSkipped += 1);
        return;
      }

      value = uniqueDatasourceValue(option, usedValues);
      option.value = value;
    }

    usedValues.add(value);
    valueSignatures.set(value, datasourceOptionSignature(option));
    normalizedOptions.push(compactObject(option));
  });

  if (options.length > 0 && normalizedOptions.length === 0) {
    addDiagnostic(context?.diagnostics || [], {
      level: 'warning',
      code: 'DATASOURCE_DEDUPED_TO_EMPTY',
      scopeId,
      message: 'El datasource quedo vacio despues de deduplicar opciones.',
    });
  }

  return normalizedOptions;
}

function getSelectCatalogCandidates(field = {}, variableName = '') {
  return [
    field.name,
    field.binding,
    field.variable,
    field.variableBase,
    variableName,
    normalizeVariableName(field.name || ''),
    normalizeVariableName(field.binding || ''),
    normalizeVariableName(field.variable || ''),
    normalizeVariableName(field.variableBase || ''),
    normalizeVariableName(variableName || ''),
  ].filter(Boolean);
}

function findCatalogForSelect(ir, field = {}, variableName = '') {
  const catalogs = ir.catalogs || {};
  const catalogValues = Object.values(catalogs);

  if (field.catalog) {
    const directCatalog = catalogs[field.catalog] || catalogValues.find((catalog) => catalog.id === field.catalog);

    if (directCatalog) {
      return directCatalog;
    }
  }

  const candidateNames = new Set(getSelectCatalogCandidates(field, variableName).map(normalizeLookupName).filter(Boolean));

  return (
    catalogValues.find((catalog) => {
      const catalogNames = [
        catalog.variable,
        catalog.sourceVariable,
        catalog.target,
        catalog.name,
      ].map(normalizeLookupName);

      return catalogNames.some((catalogName) => candidateNames.has(catalogName));
    }) || null
  );
}

function getSelectDatasource(field, ir, variableName, context) {
  const catalog = findCatalogForSelect(ir, field, variableName);
  const catalogOptions = catalog?.options || [];
  const fieldOptions = getFieldOptions(field);
  const datasource = normalizeDatasource(
    catalogOptions.length > 0 ? catalogOptions : fieldOptions,
    context,
    field.name || field.binding || field.variable || variableName
  );

  if (datasource.length === 0) {
    context.diagnostics.push({
      level: 'warning',
      code: 'SELECT_WITHOUT_CATALOG',
      field: field.name || field.binding || field.variable || variableName,
      message: 'El campo select no tiene datasource porque no se encontro catalogo en IR.catalogs.',
    });
  }

  return {
    catalog,
    datasource,
  };
}

function isDisplayField(field = {}) {
  return (
    field.type === 'display' ||
    field.controlType === 'label' ||
    field.control === 'display' ||
    field.classification === 'read_display' ||
    field.variableClass === 'read_display' ||
    (field.variable && field.visible !== false && field.capturable === false && field.type !== 'button')
  );
}

function getCommonPrefix(values = []) {
  if (values.length === 0) {
    return '';
  }

  return values.reduce((prefix, value) => {
    let index = 0;

    while (index < prefix.length && index < value.length && prefix[index] === value[index]) {
      index += 1;
    }

    return prefix.slice(0, index);
  });
}

function normalizeOptionValue(value = '') {
  return normalizeVariableName(value)
    .replace(/^@/, '')
    .replace(/[^A-Z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
}

function deriveCheckboxGroupOptions(options = []) {
  const optionVariables = options.map((option) => normalizeOptionValue(option.variable || option.name || '')).filter(Boolean);
  const commonPrefix = getCommonPrefix(optionVariables);

  return options.map((option, index) => {
    const optionVariable = normalizeOptionValue(option.variable || option.name || '');
    const structuralValue = commonPrefix && optionVariable.startsWith(commonPrefix) ? optionVariable.slice(commonPrefix.length) : '';
    const fallbackValue = normalizeOptionValue(option.value || option.label || option.variable || String(index + 1));

    return {
      text: option.text || option.label || option.variable,
      value: structuralValue || fallbackValue,
      variable: option.variable || option.name,
      name: normalizeDomName(option.variable || option.name || ''),
      binding: normalizeVariableName(option.variable || option.name || ''),
    };
  });
}

function normalizeText(value = '') {
  return value.trim().replace(/\s+/g, ' ');
}

function slugify(value = '') {
  return normalizeText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function safeMenuValue(text = '') {
  return String(text || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function createMessageName(index = 0) {
  return `msg_${index + 1}`;
}

function isSeparator(label = '') {
  return /^[-_=]{3,}$/.test(label.trim());
}

function createUniqueName(baseName, usedNames) {
  const cleanBaseName = baseName || 'field';
  let candidate = cleanBaseName;
  let index = 2;

  while (usedNames.has(candidate)) {
    candidate = `${cleanBaseName}_${index}`;
    index += 1;
  }

  usedNames.add(candidate);
  return candidate;
}

function extractTemplateVariables(template = '') {
  const variables = [];
  let match;

  PLACEHOLDER_PATTERN.lastIndex = 0;

  while ((match = PLACEHOLDER_PATTERN.exec(template)) !== null) {
    const variableName = normalizeDomName(match[1]);

    if (!variableName || variables.includes(variableName)) {
      continue;
    }

    variables.push(variableName);
  }

  return variables;
}

function extractBracketVariables(expression = '') {
  const variables = [];
  let match;

  BRACKET_REFERENCE_PATTERN.lastIndex = 0;

  while ((match = BRACKET_REFERENCE_PATTERN.exec(expression)) !== null) {
    const variableName = normalizeDomName(match[1]);

    if (!variableName || variables.includes(variableName)) {
      continue;
    }

    variables.push(variableName);
  }

  return variables;
}

function createLabelField(field, context) {
  const label = normalizeText(field.text || field.label || '');
  const separator = isSeparator(label);
  const baseName = separator ? `separator_${context.separatorIndex}` : createMessageName(context.messageIndex);

  if (separator) {
    context.separatorIndex += 1;
  } else {
    context.messageIndex += 1;
  }

  return {
    name: createUniqueName(baseName, context.usedNames),
    label,
    typedata: 'label',
    visible: true,
    readonly: true,
    displayOnly: true,
    value: '',
  };
}

function createDisplayField(field, ir, context) {
  const variableName = normalizeVariableName(field.binding || field.variable || field.name);
  const variable = findVariable(ir, variableName);
  const sourceName = normalizeDomName(variableName);

  context.visibleBindings.add(variableName.toUpperCase());

  return compactObject({
    _sourceName: sourceName,
    name: createUniqueName(sourceName, context.usedNames),
    label: field.label || variableName,
    typedata: 'label',
    visible: true,
    readonly: true,
    displayOnly: true,
    binding: variableName,
    value: '',
    templateValue: field.templateValue || `{${variableName}}`,
    textTemplate: field.textTemplate || `${field.label || variableName}: {${variableName}}`,
    datatype: field.datatype || variable?.datatype,
    maxlength: field.maxlength ?? variable?.length,
    source: field.source || variable?.source,
    classification: field.classification || field.variableClass || variable?.classification,
  });
}

function createButtonField(field, context) {
  const action = field.action || field.button || '';
  const buttonName = normalizeDomName(action || field.label || `button_${context.buttonIndex}`);

  context.buttonIndex += 1;

  return {
    _sourceName: buttonName,
    name: createUniqueName(buttonName, context.usedNames),
    label: field.label || buttonName,
    typedata: 'button',
    visible: true,
    action,
    value: '',
  };
}

function createEditableField(field, ir, context) {
  const variableName = normalizeVariableName(field.binding || field.variable || field.name);
  const variable = findVariable(ir, variableName);
  const typedata = normalizeTypedata(field, variable);
  const selectData = typedata === 'select' ? getSelectDatasource(field, ir, variableName, context) : null;
  const datasource = selectData?.datasource;
  const sourceName = normalizeDomName(variableName);

  context.visibleBindings.add(variableName.toUpperCase());

  const normalizedField = compactObject({
    _sourceName: sourceName,
    name: createUniqueName(sourceName, context.usedNames),
    label: field.label || variableName,
    typedata,
    visible: field.visible !== false,
    readonly: Boolean(field.readonly),
    displayOnly: false,
    binding: variableName,
    value: field.value ?? getInitialValue(ir, variableName),
    mandatory: field.required,
    coincidence: DEFAULT_COINCIDENCE,
    maxlength: field.maxlength ?? variable?.length,
    datatype: field.datatype || variable?.datatype,
    source: field.source || variable?.source,
    classification: field.classification || field.variableClass || variable?.classification,
    catalog: selectData?.catalog?.id || field.catalog,
    datasource,
  });

  if (typedata === 'select') {
    normalizedField.source_additional = field.source_additional || [];
  }

  return normalizedField;
}

function normalizeField(field, ir, context) {
  if (!field || field.visible === false) {
    return [];
  }

  if (field.type === 'text' || field.type === 'system') {
    return [createLabelField(field, context)];
  }

  if (isDisplayField(field)) {
    return [createDisplayField(field, ir, context)];
  }

  if (field.type === 'button') {
    return [createButtonField(field, context)];
  }

  if (field.type === 'field_row') {
    return (field.parts || []).flatMap((part) => normalizeField(part, ir, context));
  }

  if (field.type === 'checkbox-group') {
    const baseName = slugify(field.label || `checkbox_group_${context.checkboxGroupIndex}`);
    context.checkboxGroupIndex += 1;

    return [
      compactObject({
        name: createUniqueName(baseName, context.usedNames),
        label: field.label,
        typedata: 'check_group',
        visible: field.visible !== false,
        readonly: false,
        displayOnly: false,
        value: '',
        options: deriveCheckboxGroupOptions(field.options || []).map((option) => {
          context.visibleBindings.add(normalizeVariableName(option.variable).toUpperCase());
          return option;
        }),
      }),
    ];
  }

  if (field.variable) {
    return [createEditableField(field, ir, context)];
  }

  return [];
}

function createConditionRef(action, ir) {
  const conditions = action.conditions || [];
  const buttonCondition = conditions.find((condition) => {
    return condition.left === '@BTN_PRSD' && condition.operator === '=' && condition.rightType === 'literal';
  });

  if (buttonCondition) {
    const matchingButton = Object.values(ir.buttonDefinitions || {}).find((buttonDefinition) => {
      return buttonDefinition.label === buttonCondition.right;
    });

    if (matchingButton) {
      return normalizeDomName(matchingButton.button);
    }
  }

  const conditionText = conditions
    .map((condition) => {
      if (condition.join) {
        return condition.join;
      }

      return `${condition.left}${condition.operator}${condition.right}`;
    })
    .join('');

  return conditionText ? `if:${conditionText}` : null;
}

function collectRemarkActions(actions = [], ir, conditionPath = []) {
  return actions.flatMap((action) => {
    if (!action) {
      return [];
    }

    if (action.type === 'remark') {
      return [
        {
          ...action,
          conditionRef: conditionPath[conditionPath.length - 1],
          conditionPath,
        },
      ];
    }

    if (action.type === 'ssr') {
      return [
        {
          type: 'ssr',
          template: action.template || action.expression,
          remarkType: 'SSR',
          skip: action.skip !== false,
          conditionRef: conditionPath[conditionPath.length - 1],
          conditionPath,
        },
      ];
    }

    if (action.type === 'if') {
      const conditionRef = createConditionRef(action, ir);
      const nextConditionPath = conditionRef ? [...conditionPath, conditionRef] : conditionPath;
      const thenRemarks = collectRemarkActions(action.then || [], ir, nextConditionPath);
      const elseRemarks = collectRemarkActions(action.else || [], ir, conditionRef ? [...conditionPath, `${conditionRef}:else`] : conditionPath);

      return [...thenRemarks, ...elseRemarks];
    }

    return [];
  });
}

function collectRemarks(ir) {
  const blockRemarks = Object.values(ir.blocks || {}).flatMap((block) => collectRemarkActions(block.actions || [], ir));

  if (blockRemarks.length > 0) {
    return blockRemarks;
  }

  return ir.remarks || [];
}

function getRemarkBlock(template = '') {
  const blockMatch = template.match(/^(\d+)/);

  return blockMatch ? Number(blockMatch[1]) : null;
}

function normalizeRemark(remark) {
  const template = remark.template || remark.remark || remark.text || '';

  return compactObject({
    block: getRemarkBlock(template),
    typeremark: remark.remarkType || remark.typeremark || 'Historical',
    remark: template,
    remark_mask: template,
    skip: remark.skip !== false,
    processed: false,
    campoForm: extractTemplateVariables(template),
    coincidence: DEFAULT_COINCIDENCE,
    conditionRef: remark.conditionRef,
    conditionPath: remark.conditionPath && remark.conditionPath.length > 1 ? remark.conditionPath : undefined,
  });
}

function dedupeRemarks(remarks) {
  const seen = new Set();

  return remarks.filter((remark) => {
    const key = [remark.remark, remark.conditionRef || '', JSON.stringify(remark.conditionPath || [])].join('|');

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function addVariableReference(target, variableName) {
  const rawName = normalizeVariableName(variableName);
  const normalizedName = rawName.startsWith('@') ? rawName : normalizeDomName(rawName);

  if (!normalizedName) {
    return;
  }

  target.add(normalizedName.toUpperCase());
}

function collectConditionVariables(conditions = [], target) {
  conditions.forEach((condition) => {
    if (condition.join) {
      return;
    }

    addVariableReference(target, condition.left);

    if (condition.rightType === 'variable') {
      addVariableReference(target, condition.right);
    }
  });
}

function collectActionVariables(action, target) {
  if (!action) {
    return;
  }

  if (['command', 'remark', 'ssr'].includes(action.type)) {
    extractTemplateVariables(action.template || action.expression || '').forEach((variableName) => addVariableReference(target, variableName));
  }

  if (action.type === 'read') {
    (action.targets || [action.target]).filter(Boolean).forEach((variableName) => addVariableReference(target, variableName));
  }

  if (action.type === 'default') {
    addVariableReference(target, action.target);

    if (action.valueType === 'variable') {
      addVariableReference(target, action.value);
    }
  }

  if (action.type === 'if') {
    collectConditionVariables(action.conditions || [], target);
    (action.then || []).forEach((childAction) => collectActionVariables(childAction, target));
    (action.else || []).forEach((childAction) => collectActionVariables(childAction, target));
  }

  if (['compute', 'require', 'write'].includes(action.type)) {
    extractBracketVariables(action.expression || '').forEach((variableName) => addVariableReference(target, variableName));
  }
}

function collectLogicVariables(ir) {
  const variables = new Set();

  Object.values(ir.blocks || {}).forEach((block) => {
    (block.actions || []).forEach((action) => collectActionVariables(action, variables));
  });

  if (variables.size === 0) {
    (ir.logic || []).forEach((action) => collectActionVariables(action, variables));
  }

  return variables;
}

function createHiddenField(variableName, ir, context) {
  const normalizedName = normalizeVariableName(variableName);
  const domName = normalizeDomName(normalizedName);
  const variable = findVariable(ir, normalizedName);
  const value = getInitialValue(ir, normalizedName);
  const isSystem = String(variableName).startsWith('@');
  const source = isSystem ? 'system' : variable?.source === 'default' && variable?.line ? 'define' : variable?.source || 'define';

  return compactObject({
    _sourceName: domName,
    name: createUniqueName(domName, context.usedNames),
    label: domName,
    typedata: normalizeTypedata({}, variable),
    visible: false,
    readonly: true,
    value,
    maxlength: variable?.length,
    datatype: variable?.datatype,
    source,
    systemName: isSystem ? variableName : undefined,
  });
}

function appendHiddenFields(fields, remarks, ir, context) {
  const referencedVariables = new Set([...remarks.flatMap((remark) => remark.campoForm || []), ...collectLogicVariables(ir)]);
  const hiddenFields = [];

  referencedVariables.forEach((variableName) => {
    const normalizedName = normalizeDomName(variableName).toUpperCase();

    if (!normalizedName || context.visibleBindings.has(normalizedName)) {
      return;
    }

    const variable = findVariable(ir, normalizedName);
    const isSystem = String(variableName).startsWith('@');

    if (!variable && !isSystem) {
      return;
    }

    hiddenFields.push(createHiddenField(isSystem ? variableName : normalizedName, ir, context));
    context.visibleBindings.add(normalizedName);
  });

  return [...fields, ...hiddenFields];
}

function createFormContext(diagnostics = [], duplicates = createDuplicateReport()) {
  return {
    buttonIndex: 1,
    checkboxGroupIndex: 1,
    messageIndex: 0,
    separatorIndex: 1,
    usedNames: new Set(),
    visibleBindings: new Set(),
    diagnostics,
    duplicates,
  };
}

function getMenuActionRef(item = {}) {
  const submenuAction = (item.actions || []).find((action) => action.type === 'submenu') || (item.action?.type === 'submenu' ? item.action : null);
  const navigationAction = (item.actions || []).find((action) => ['call', 'goto', 'exit'].includes(action.type)) || item.action;

  if (submenuAction) {
    return compactObject({
      type: 'submenu',
      target: submenuAction.targetId || submenuAction.target,
      title: submenuAction.title,
    });
  }

  if (navigationAction && ['call', 'goto', 'exit'].includes(navigationAction.type)) {
    return compactObject({
      type: navigationAction.type,
      target: navigationAction.target,
    });
  }

  return {
    type: 'menu_option',
  };
}

function getMenuFormId(menu = {}) {
  return safeMenuValue(menu.id || menu.key || menu.title || 'MENU') || 'MENU';
}

function getMenuItems(menu = {}) {
  return menu.items || menu.options || [];
}

function getMenuOptionActionType(item = {}) {
  if (['submenu', 'actions', 'call', 'goto', 'exit', 'showWindow'].includes(item.actionType)) {
    return item.actionType;
  }

  const actions = item.actions || [];
  const submenuAction = actions.find((action) => action.type === 'submenu') || (item.action?.type === 'submenu' ? item.action : null);
  const executableActions = actions.filter((action) => action.type !== 'submenu');

  if (submenuAction) {
    return 'submenu';
  }

  if (executableActions.length === 0) {
    return 'unknown';
  }

  if (executableActions.length === 1 && ['call', 'goto', 'exit', 'window', 'showWindow'].includes(executableActions[0].type)) {
    return executableActions[0].type === 'window' ? 'showWindow' : executableActions[0].type;
  }

  return 'actions';
}

function getMenuOptionTarget(item = {}) {
  const actions = item.actions || [];
  const submenuAction = actions.find((action) => action.type === 'submenu') || (item.action?.type === 'submenu' ? item.action : null);

  if (submenuAction) {
    return submenuAction.targetId || submenuAction.target || item.target || null;
  }

  const action = actions.find((candidate) => ['call', 'goto', 'window', 'showWindow'].includes(candidate.type)) || item.action;

  if (!action) {
    return item.target || null;
  }

  return action.target || action.targetId || action.formRef || action.id || item.target || null;
}

function getMenuOptionActionRef(menu, item) {
  const actionType = getMenuOptionActionType(item);

  if (actionType !== 'actions') {
    return item.actionRef;
  }

  return item.actionRef || `${getMenuFormId(menu)}_OPTION_${safeMenuValue(item.text || item.value)}`;
}

function normalizeMenuDatasourceItem(menu, item) {
  const actionType = getMenuOptionActionType(item);

  return compactObject({
    text: item.text,
    value: item.value || safeMenuValue(item.text),
    actionType,
    target: actionType === 'actions' ? null : getMenuOptionTarget(item),
    actionRef: getMenuOptionActionRef(menu, item),
  });
}

function collectMenus(menus = []) {
  const collected = [];
  const seenMenuIds = new Set();

  function visit(menu) {
    if (!menu) {
      return;
    }

    const menuId = getMenuFormId(menu);

    if (seenMenuIds.has(menuId)) {
      return;
    }

    seenMenuIds.add(menuId);
    collected.push(menu);
    (menu.items || []).forEach((item) => {
      if (item.submenu) {
        visit(item.submenu);
        return;
      }

      (item.actions || []).forEach((action) => {
        if (action.type === 'submenu' && action.menu) {
          visit(action.menu);
        }
      });
    });
  }

  menus.forEach(visit);

  return collected;
}

function getMenuRoots(ir = {}) {
  const menus = ir.menus || [];

  if ((ir.menuTree || []).length > 0) {
    return ir.menuTree;
  }

  const roots = menus.filter((menu) => !menu.parentKey && !menu.parent && !menu.parentId);

  return roots.length > 0 ? roots : menus;
}

function stripPrivateFieldMetadata(field = {}) {
  const { _sourceName, ...publicField } = field;

  return publicField;
}

function dedupeFields(fields = [], formId = '', context = null) {
  const seenFieldNames = new Set();
  const dedupedFields = [];

  fields.forEach((field) => {
    const fieldName = field._sourceName || field.name;

    if (!fieldName) {
      dedupedFields.push(stripPrivateFieldMetadata(field));
      return;
    }

    if (seenFieldNames.has(fieldName)) {
      context?.duplicates && (context.duplicates.fieldsSkipped += 1);
      addDiagnostic(context?.diagnostics || [], {
        level: 'warning',
        code: 'DUPLICATE_FIELD_SKIPPED',
        formId,
        field: field.name,
        message: 'Se omitio un campo duplicado dentro del mismo form.',
      });
      return;
    }

    seenFieldNames.add(fieldName);
    dedupedFields.push(stripPrivateFieldMetadata(field));
  });

  return dedupedFields;
}

function dedupeForms(forms = [], context = null) {
  const seenFormIds = new Set();
  const dedupedForms = [];

  forms.forEach((form) => {
    const formId = form.id || form.title;

    if (!formId) {
      dedupedForms.push(form);
      return;
    }

    if (seenFormIds.has(formId)) {
      context?.duplicates && (context.duplicates.formsSkipped += 1);
      addDiagnostic(context?.diagnostics || [], {
        level: 'warning',
        code: 'DUPLICATE_FORM_SKIPPED',
        formId,
        message: 'Se omitio un form duplicado en data.json.',
      });
      return;
    }

    seenFormIds.add(formId);
    dedupedForms.push(form);
  });

  return dedupedForms;
}

function normalizeMenuForm(menu, context = { diagnostics: [], duplicates: createDuplicateReport() }) {
  const menuId = getMenuFormId(menu);
  const selectName = `${menuId}_SELECT`;
  const items = getMenuItems(menu).filter((item) => item?.visible !== false);
  const datasource = dedupeDatasourceOptions(
    items.map((item) => normalizeMenuDatasourceItem(menu, item)),
    context,
    menuId
  );

  if (datasource.length === 0) {
    context.diagnostics.push({
      level: 'warning',
      code: 'MENU_WITHOUT_OPTIONS',
      menuId,
      message: 'Se detecto MENU, pero no se encontraron opciones.',
    });
  }

  datasource.forEach((option) => {
    if (option.actionType === 'unknown') {
      context.diagnostics.push({
        level: 'warning',
        code: 'MENU_OPTION_WITHOUT_ACTION',
        menuId,
        option: option.text,
        message: 'La opcion de MENU no tiene una accion clara.',
      });
    }
  });

  return compactObject({
    id: menuId,
    sourceId: menu.id,
    title: menu.title || menu.id,
    title_visible: true,
    windowType: 'menu',
    visible: menu.visible !== false,
    parent: menu.parentId || menu.parent || undefined,
    fields: [
      compactObject({
        name: selectName,
        label: menu.title || menu.id || 'MENU',
        typedata: 'select_menu',
        visible: true,
        mandatory: true,
        value: '',
        datasource,
      }),
      {
        name: `${menuId}_CONTINUE`,
        label: 'Continuar',
        typedata: 'button_menu',
        visible: true,
        action: 'continue_menu',
        targetField: selectName,
        actionRef: {
          type: 'menu_continue',
          targetField: selectName,
          menuId,
        },
        value: '',
      },
    ],
  });
}

function normalizeForm(form, ir, remarks, diagnostics, duplicates) {
  const context = createFormContext(diagnostics, duplicates);
  const fields = (form.fields || []).flatMap((field) => normalizeField(field, ir, context));
  const fieldsWithHidden = appendHiddenFields(fields, remarks, ir, context);
  const dedupedFields = dedupeFields(fieldsWithHidden, form.id, context);

  return compactObject({
    id: form.id,
    title: form.title,
    title_visible: true,
    windowType: form.windowType,
    visible: form.visible !== false,
    fieldCount: form.fieldCount,
    messageCount: form.messageCount,
    buttonCount: form.buttonCount,
    fields: dedupedFields,
  });
}

export function generateDataJson(ir = {}) {
  const diagnostics = [];
  const duplicates = createDuplicateReport();
  const diagnosticsContext = { diagnostics, duplicates };
  const remarks = dedupeRemarks(collectRemarks(ir).map(normalizeRemark));
  const menuForms = collectMenus(getMenuRoots(ir)).map((menu) => normalizeMenuForm(menu, diagnosticsContext));
  const forms = (ir.forms || []).map((form) => normalizeForm(form, ir, remarks, diagnostics, duplicates));
  const allForms = dedupeForms([...forms, ...menuForms], diagnosticsContext);
  const dataJson = compactObject({
    title: ir.title,
    editCatalogues: false,
    reintentos: 0,
    sendCommands: false,
    validateActiveReservation: false,
    forms: allForms,
    outputs: remarks.length > 0 ? { remarks } : undefined,
    diagnostics: {
      warnings: diagnostics,
      duplicates,
    },
  });
  const serializedLineCount = JSON.stringify(dataJson, null, 2).split('\n').length;

  if (allForms.length > 300) {
    diagnostics.push({
      level: 'warning',
      code: 'DATA_JSON_TOO_MANY_FORMS',
      message: 'data.json contiene mas de 300 forms; revisar duplicados o menus expansivos.',
      forms: allForms.length,
    });
  }

  if (serializedLineCount > 20000) {
    diagnostics.push({
      level: 'warning',
      code: 'DATA_JSON_TOO_LARGE',
      message: 'data.json parece contener duplicados o logica embebida.',
      lines: serializedLineCount,
    });
  }

  return dataJson;
}
